import { useCallback } from "react";
import { mutate } from "swr";
import useSWRImmutable from "swr/immutable";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  Calculation,
  InsertCalculationBody,
  SearchAdminCalculation,
  SearchCalculation,
  UpdateCalculationBody,
} from "./types/calculations";

const latestUpdateRequestByUrl = new Map<string, number>();

export const useCalculationsApi = () => {
  const api = useApi();

  // only admin endpoint
  const useAdminCalculations = useCallback(
    (query: PaginationQuery, signal: AbortSignal, moduleId?: string) => {
      const baseUrl = `/admin/calculations${moduleId ? `?moduleId=${encodeURIComponent(moduleId)}` : ""}`;
      const url = buildUrl(baseUrl, query);
      return useSWRImmutable<Paginated<SearchAdminCalculation>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchAdminCalculation>>({ url, signal }),
      );
    },
    [api],
  );

  // both admin and user endpoint
  const useProjectCalculations = useCallback(
    (projectId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(`${isAdmin ? "/admin" : ""}/projects/${projectId}/calculations`, query);
      return useSWRImmutable<Paginated<SearchCalculation>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchCalculation>>({ url, signal }),
      );
    },
    [api],
  );

  // both admin and user endpoint
  const useCalculation = useCallback(
    (calculationId: string, signal: AbortSignal, isAdmin?: boolean) => {
      const url = isAdmin ? `/admin/calculations/${calculationId}` : `/calculations/${calculationId}`;
      return useSWRImmutable<Calculation, CustomError, string>(url, (url) => api.get<Calculation>({ url, signal }));
    },
    [api],
  );

  // only user endpoint
  const useCalculations = useCallback(
    (query: PaginationQuery, signal: AbortSignal, moduleId?: string) => {
      const baseUrl = `/calculations${moduleId ? `?moduleId=${encodeURIComponent(moduleId)}` : ""}`;
      const url = buildUrl(baseUrl, query);
      return useSWRImmutable<Paginated<SearchCalculation>, CustomError, string>(url, (url) =>
        api.get<Paginated<SearchCalculation>>({ url, signal }),
      );
    },
    [api],
  );

  const insertCalculation = useCallback(
    async (body: InsertCalculationBody, isAdmin?: boolean): Promise<{ id: string }> => {
      const url = isAdmin ? `/admin/calculations` : `/calculations`;
      const id = await api.post<{ id: string }, InsertCalculationBody>(url, body);
      mutateCache([
        "/projects",
        "/calculations",
        "/admin/projects",
        "/admin/calculations",
        `/projects/${body.projectId}/calculations`,
        `/admin/projects/${body.projectId}/calculations`,
      ]);
      return id;
    },
    [api],
  );

  const updateCalculation = useCallback(
    async <TRequest, TResponse = TRequest>(
      projectId: string | undefined,
      calculationId: string,
      body: UpdateCalculationBody<TRequest>,
      optimisticData: Calculation<TResponse>,
      isAdmin?: boolean,
    ): Promise<Calculation<TResponse>> => {
      const url = isAdmin ? `/admin/calculations/${calculationId}` : `/calculations/${calculationId}`;
      const requestId = (latestUpdateRequestByUrl.get(url) ?? 0) + 1;
      latestUpdateRequestByUrl.set(url, requestId);
      mutate(url, optimisticData, { revalidate: false, populateCache: true }); // tested works
      const updated = await api.putWithResult<UpdateCalculationBody<TRequest>, Calculation<TResponse>>(url, body);
      // Drop this response if a newer update for the same calculation was dispatched meanwhile,
      // otherwise its stale data would clobber the more recent optimistic/server state.
      if (latestUpdateRequestByUrl.get(url) === requestId) {
        mutate(url, updated, { revalidate: false, populateCache: true });
      }
      mutateCache(
        [
          "/projects",
          "/calculations",
          "/admin/projects",
          "/admin/calculations",
          `/projects/${projectId}/calculations`,
          `/calculations/${calculationId}/items`,
          `/admin/projects/${projectId}/calculations`,
        ],
        url,
      );
      return updated;
    },
    [api],
  );

  const deleteCalculation = useCallback(
    async (projectId: string | undefined, calculationId: string, isAdmin?: boolean): Promise<void> => {
      const url = isAdmin ? `/admin/calculations/${calculationId}` : `/calculations/${calculationId}`;
      await api.del(url).then(() => {
        mutateCache([
          "/projects",
          `/projects/${projectId}/calculations`,
          "/calculations",
          `/calculations/${calculationId}`,
          "/admin/projects",
          "/admin/calculations",
          `/admin/projects/${projectId}/calculations`,
          `/admin/projects/${projectId}/calculations/${calculationId}`,
        ]);
      });
    },
    [api],
  );

  const duplicateCalculation = useCallback(
    async (
      projectId: string | undefined,
      calculationId: string,
      body: { projectId?: string; name: string },
      isAdmin?: boolean,
    ): Promise<void> => {
      const url = isAdmin
        ? `/admin/calculations/${calculationId}/duplicate`
        : `/calculations/${calculationId}/duplicate`;
      await api.post(url, body).then(() => {
        mutateCache([
          "/projects",
          `/projects/${projectId}/calculations`,
          "/calculations",
          `/calculations/${calculationId}`,
          "/admin/projects",
          `/admin/projects/${projectId}/calculations`,
          "/admin/calculations",
          `/admin/calculations/${calculationId}`,
        ]);
      });
    },
    [api],
  );

  return {
    // admin endpoint
    useAdminCalculations,

    useProjectCalculations,
    useCalculation,
    useCalculations,

    //  user endpoints
    insertCalculation,
    updateCalculation,
    deleteCalculation,

    // both admin and user endpoint
    duplicateCalculation,
  };
};
